/**
 * Änderungsverlauf: who changed the e-mail settings, what and when (T1b).
 *
 * THE READ SIDE OF THE SETTINGS AUDIT (T1a, Regel 49). One entry per save that
 * changed something, newest first, from `GET /api/admin/email-settings/activity`.
 * The values ARE shown: they are this organisation's own sender and test
 * addresses, which the person can read on the form right above, and the endpoint
 * serves exactly them — nothing else from the audit row (no IP address, user
 * agent or request id) reaches this page.
 *
 * READABLE WITHOUT COLOUR. Each change is a term ("Absender-E-Mail") with a
 * "Vorher" and a "Nachher" value, both named in text; the arrow between them is
 * decoration and hidden from assistive technology. A side that was not set says
 * so ("nicht gesetzt") instead of leaving a blank a reader could take for a
 * rendering fault.
 *
 * NO PAGER, BECAUSE THE ENDPOINT HAS NONE. It answers its newest capped page and
 * the full `total`; when the two differ the card says how many it shows of how
 * many, as the Automation Center's Protokoll does.
 *
 * LONG VALUES WRAP. An address or a name can be one unbroken 300-character
 * string; every value breaks anywhere rather than pushing the page sideways.
 */
import React from "react";
import { ArrowDown, ArrowRight } from "lucide-react";

import { formatDateTime } from "../../../utils/datetime";
import { activityActorText } from "../automations/AutomationProtocolTab";
import { activityChanges } from "./emailSettingsModel";

const CARD =
  "flex min-w-0 flex-col gap-4 rounded-[14px] border border-border bg-card p-4 md:p-5";
const MUTED = "m-0 text-[12.5px] text-muted-foreground";
const RETRY =
  "inline-flex h-9 items-center rounded-[9px] border border-border bg-card px-3 text-[12.5px] font-semibold " +
  "text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const ERROR =
  "m-0 border-l-2 border-destructive pl-2 text-[12.5px] leading-snug text-foreground";
const CAPTION = "block text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
const VALUE = "mt-0.5 block break-all text-[12.5px] text-foreground [overflow-wrap:anywhere]";

function ChangeValue({ caption, value }) {
  return (
    <div className="min-w-0 rounded-[8px] border border-border bg-background px-2.5 py-2">
      <span className={CAPTION}>{caption}</span>
      {value === null ? (
        <span className="mt-0.5 block text-[12.5px] italic text-muted-foreground">
          nicht gesetzt
        </span>
      ) : (
        <span className={VALUE}>{value}</span>
      )}
    </div>
  );
}

function ActivityEntry({ entry }) {
  const changes = activityChanges(entry);
  return (
    <li className="flex min-w-0 flex-col gap-3 border-b border-border py-4 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="m-0 min-w-0 break-words text-[12.5px] text-foreground">
          Geändert durch{" "}
          <span className="font-semibold">{activityActorText(entry)}</span>
        </p>
        <time
          dateTime={entry.created_at}
          className="shrink-0 text-[11.5px] tabular-nums text-muted-foreground"
        >
          {formatDateTime(entry.created_at)}
        </time>
      </div>
      {changes.length ? (
        <dl className="m-0 flex flex-col gap-3">
          {changes.map((change) => (
            <div key={change.field} className="min-w-0">
              <dt className="text-[12px] font-medium text-foreground">{change.label}</dt>
              <dd className="m-0 mt-1.5 grid grid-cols-1 items-center gap-1.5 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                <ChangeValue caption="Vorher" value={change.before} />
                <span className="flex justify-center text-muted-foreground" aria-hidden="true">
                  <ArrowDown className="h-3.5 w-3.5 sm:hidden" />
                  <ArrowRight className="hidden h-3.5 w-3.5 sm:block" />
                </span>
                <ChangeValue caption="Nachher" value={change.after} />
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className={MUTED}>Für diese Änderung sind keine Felddetails gespeichert.</p>
      )}
    </li>
  );
}

export default function EmailSettingsActivity({ items, total, loading, error, onRetry }) {
  let body;
  if (loading) {
    body = <p className={MUTED}>Änderungsverlauf wird geladen …</p>;
  } else if (error) {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className={ERROR}>
          {error}
        </p>
        <button type="button" onClick={onRetry} className={RETRY}>
          Erneut versuchen
        </button>
      </div>
    );
  } else if (!items.length) {
    body = <p className={MUTED}>Noch keine Änderungen gespeichert.</p>;
  } else {
    body = (
      <>
        <ol aria-label="Änderungsverlauf" className="m-0 flex list-none flex-col p-0">
          {items.map((entry) => (
            <ActivityEntry key={entry.id} entry={entry} />
          ))}
        </ol>
        {total > items.length ? (
          <p className="m-0 text-[11.5px] text-muted-foreground">
            Die neuesten {items.length} von {total} Änderungen.
          </p>
        ) : null}
      </>
    );
  }

  return (
    <section aria-labelledby="email-settings-activity-title" className={CARD}>
      <div>
        <h2 id="email-settings-activity-title" className="m-0 text-[14px] font-semibold text-foreground">
          Änderungsverlauf
        </h2>
        <p className="m-0 mt-1 text-[12.5px] leading-snug text-muted-foreground">
          Jede gespeicherte Änderung mit Zeitpunkt, Person und den Werten vorher und
          nachher.
        </p>
      </div>
      {body}
    </section>
  );
}
